'use client';

import { useEffect, useState, useRef } from 'react';
import AudioBookMobilePlayer from './mobile/AudioBookMobilePlayer';

interface AudioBookViewportRouterProps {
  audioBookId: string;
  isBilingual: boolean;
  desktopViewer: React.ReactNode;
}

// Shared state storage for preserving playback state across viewport changes
interface SharedPlayerState {
  currentPageIndex: number;
  isPlaying: boolean;
  currentTime: number;
  hasStarted: boolean;
  audioSrc: string | null;
}

export default function AudioBookViewportRouter({
  audioBookId,
  isBilingual,
  desktopViewer
}: AudioBookViewportRouterProps) {
  // 🔧 FIX: Detect viewport size and conditionally render ONLY ONE player
  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  
  // 🔧 CRITICAL FIX: Preserve playback state across viewport changes
  const sharedStateRef = useRef<SharedPlayerState>({
    currentPageIndex: 0,
    isPlaying: false,
    currentTime: 0,
    hasStarted: false,
    audioSrc: null
  });

  // 🚨 EMERGENCY FIX: Clear stale sessionStorage on mount
  useEffect(() => {
    const stateKey = `audiobook-state-${audioBookId}`;
    const savedState = sessionStorage.getItem(stateKey);
    
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        // If state is older than 5 minutes, clear it
        const now = Date.now();
        const stateAge = now - (parsed.timestamp || 0);
        if (stateAge > 5 * 60 * 1000 || !parsed.timestamp) {
          console.log('[ViewportRouter] 🧹 Clearing stale state (age:', stateAge, 'ms)');
          sessionStorage.removeItem(stateKey);
        }
      } catch (e) {
        console.error('[ViewportRouter] Failed to parse saved state, clearing:', e);
        sessionStorage.removeItem(stateKey);
      }
    }
  }, [audioBookId]);

  useEffect(() => {
    // Initial check
    const checkViewport = () => {
      const mobile = window.innerWidth < 768;
      const prevMobile = isMobile;
      
      console.log('[ViewportRouter] Viewport check - width:', window.innerWidth, 'isMobile:', mobile);
      
      // If viewport changed and we had a previous state
      if (prevMobile !== null && prevMobile !== mobile) {
        console.log('[ViewportRouter] 🔄 Viewport changed! Preserving state:', sharedStateRef.current);
      }
      
      setIsMobile(mobile);
    };

    // Check on mount
    checkViewport();

    // Listen for resize events with debounce to prevent rapid switches
    let resizeTimer: NodeJS.Timeout;
    const debouncedCheckViewport = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkViewport, 150);
    };

    window.addEventListener('resize', debouncedCheckViewport);

    return () => {
      window.removeEventListener('resize', debouncedCheckViewport);
      clearTimeout(resizeTimer);
    };
  }, [isMobile]);

  // Hide navbar and footer when player is active
  useEffect(() => {
    document.body.classList.add('audio-player-active');
    
    return () => {
      document.body.classList.remove('audio-player-active');
    };
  }, []);
  
  // 🔧 CRITICAL FIX: Save/restore state from sessionStorage
  useEffect(() => {
    const stateKey = `audiobook-state-${audioBookId}`;
    
    // Listen for custom events from players to save state
    const handleStateSave = (event: CustomEvent) => {
      const state = {
        ...event.detail,
        timestamp: Date.now() // Add timestamp for staleness detection
      };
      sharedStateRef.current = state;
      sessionStorage.setItem(stateKey, JSON.stringify(state));
      console.log('[ViewportRouter] 💾 Saved state:', state);
    };
    
    window.addEventListener('audiobook-state-update' as any, handleStateSave);
    
    // Try to restore state on mount
    const savedState = sessionStorage.getItem(stateKey);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        sharedStateRef.current = parsed;
        console.log('[ViewportRouter] 📥 Restored state:', parsed);
        
        // Dispatch event to notify player to restore state
        window.dispatchEvent(new CustomEvent('audiobook-state-restore', {
          detail: parsed
        }));
      } catch (e) {
        console.error('[ViewportRouter] Failed to parse saved state:', e);
      }
    }
    
    return () => {
      window.removeEventListener('audiobook-state-update' as any, handleStateSave);
    };
  }, [audioBookId]);

  // Show loading state while detecting viewport
  if (isMobile === null) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        fontSize: '1.2rem'
      }}>
        <div>Loading player...</div>
      </div>
    );
  }

  return (
    <>
      {/* 🔧 FIXED: Conditionally render ONLY ONE player based on actual viewport size */}
      {isMobile ? (
        // Mobile View (< 768px) - Netflix-style player
        <AudioBookMobilePlayer 
          audioBookId={audioBookId} 
          isBilingual={isBilingual}
          // Pass a key to force remount only when audioBookId changes, not on viewport change
          key={`mobile-${audioBookId}`}
        />
      ) : (
        // Desktop View (>= 768px) - Keep existing viewers
        desktopViewer
      )}

      <style jsx global>{`
        /* Hide navbar and footer on audio player pages */
        body.audio-player-active nav,
        body.audio-player-active header,
        body.audio-player-active footer {
          display: none !important;
        }

        /* Full height for player */
        body.audio-player-active {
          overflow: hidden;
        }
      `}</style>
    </>
  );
}

